// MSA rate sheet line editor — shared by Requests (AM review/confirm) and
// Estimating (leadership verify). Two tables: labor rates and equipment
// rates. The equipment catalog match is the load-bearing field: it converts
// the MSA's wording into our catalog item once, so every later auto-populate
// is exact.

import { styles, fmtMoney, fmtDate } from './UI';

const num = (v) => (v === '' || v == null ? null : Number(v));

export default function MsaSheetEditor({ sheet, catalog, readOnly, saving, onLineUpdate, onLineDelete, onLineAdd }) {
  const labor = (sheet.lines || []).filter(l => l.kind === 'labor');
  const equip = (sheet.lines || []).filter(l => l.kind === 'equipment');

  const moneyCell = (line, field, width = 75) => (
    readOnly
      ? <span style={{ fontSize: 12 }}>{line[field] != null ? fmtMoney(line[field]) : '—'}</span>
      : <input type="number" step="0.01" style={{ ...styles.input, width, fontSize: 12 }}
          defaultValue={line[field] ?? ''}
          onBlur={e => { if (String(line[field] ?? '') !== e.target.value) onLineUpdate(line.id, { [field]: num(e.target.value) }); }} />
  );
  const textCell = (line, field, width = 160) => (
    readOnly
      ? <span style={{ fontSize: 12 }}>{line[field] || '—'}</span>
      : <input style={{ ...styles.input, width, fontSize: 12 }}
          defaultValue={line[field] || ''}
          onBlur={e => { if ((line[field] || '') !== e.target.value) onLineUpdate(line.id, { [field]: e.target.value || null }); }} />
  );

  return (
    <div>
      <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <span><strong>Source:</strong> {sheet.source_filename || '—'}</span>
        <span><strong>Effective:</strong> {sheet.effective_date ? fmtDate(sheet.effective_date) : '—'}</span>
        <span><strong>Expires:</strong> {sheet.expiry_date ? fmtDate(sheet.expiry_date) : '—'}</span>
        {sheet.extraction_model && <span><strong>Extracted by:</strong> {sheet.extraction_model}</span>}
        {sheet.confirmed_by_name && <span><strong>Confirmed:</strong> {sheet.confirmed_by_name}</span>}
        {sheet.verified_by_name && <span><strong>Verified:</strong> {sheet.verified_by_name}</span>}
      </div>

      {/* Labor rates */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0 }}>Labor Rates ({labor.length})</h3>
        {!readOnly && (
          <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 10px' }} disabled={saving}
            onClick={() => onLineAdd({ kind: 'labor', msa_description: 'New craft' })}>+ Add Labor Line</button>
        )}
      </div>
      <div style={{ overflowX: 'auto', marginBottom: 18 }}>
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>MSA Wording</th>
            <th style={styles.th}>Our Craft</th>
            <th style={styles.th}>Pay ST</th>
            <th style={styles.th}>Pay OT</th>
            <th style={styles.th}>Bill ST</th>
            <th style={styles.th}>Bill OT</th>
            <th style={styles.th}>Per Diem</th>
            <th style={styles.th}>Notes</th>
            {!readOnly && <th style={styles.th}></th>}
          </tr></thead>
          <tbody>
            {labor.map(line => (
              <tr key={line.id}>
                <td style={styles.td}>{textCell(line, 'msa_description', 170)}</td>
                <td style={styles.td}>{textCell(line, 'craft', 130)}</td>
                <td style={styles.td}>{moneyCell(line, 'pay_st')}</td>
                <td style={styles.td}>{moneyCell(line, 'pay_ot')}</td>
                <td style={styles.td}>{moneyCell(line, 'bill_st')}</td>
                <td style={styles.td}>{moneyCell(line, 'bill_ot')}</td>
                <td style={styles.td}>{moneyCell(line, 'per_diem', 65)}</td>
                <td style={styles.td}>{textCell(line, 'notes', 150)}</td>
                {!readOnly && (
                  <td style={styles.td}>
                    <button style={{ ...styles.btnDanger, fontSize: 11, padding: '3px 8px' }} disabled={saving}
                      onClick={() => onLineDelete(line.id)}>Del</button>
                  </td>
                )}
              </tr>
            ))}
            {labor.length === 0 && (
              <tr><td colSpan={readOnly ? 8 : 9} style={{ ...styles.td, textAlign: 'center', color: '#9CA3AF' }}>
                No labor rates extracted.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Equipment rates */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0 }}>Equipment Rates ({equip.length})</h3>
        {!readOnly && (
          <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 10px' }} disabled={saving}
            onClick={() => onLineAdd({ kind: 'equipment', msa_description: 'New equipment' })}>+ Add Equipment Line</button>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>MSA Wording</th>
            <th style={styles.th}>Catalog Match</th>
            <th style={styles.th}>Usage Rate</th>
            <th style={styles.th}>Unit</th>
            <th style={styles.th}>Notes</th>
            {!readOnly && <th style={styles.th}></th>}
          </tr></thead>
          <tbody>
            {equip.map(line => (
              <tr key={line.id}>
                <td style={styles.td}>{textCell(line, 'msa_description', 200)}</td>
                <td style={styles.td}>
                  {readOnly ? (
                    <span style={{ fontSize: 12, color: line.catalog_id ? '#1F2937' : '#B45309' }}>
                      {line.catalog_description || '⚠ no match'}
                    </span>
                  ) : (
                    <select style={{ ...styles.input, width: 220, fontSize: 12 }}
                      value={line.catalog_id || ''}
                      onChange={e => onLineUpdate(line.id, { catalog_id: e.target.value ? Number(e.target.value) : null })}>
                      <option value="">— no catalog match —</option>
                      {(catalog || []).map(c => <option key={c.id} value={c.id}>{c.description}</option>)}
                    </select>
                  )}
                </td>
                <td style={styles.td}>{moneyCell(line, 'day_rate', 80)}</td>
                <td style={styles.td}>{textCell(line, 'unit', 70)}</td>
                <td style={styles.td}>{textCell(line, 'notes', 160)}</td>
                {!readOnly && (
                  <td style={styles.td}>
                    <button style={{ ...styles.btnDanger, fontSize: 11, padding: '3px 8px' }} disabled={saving}
                      onClick={() => onLineDelete(line.id)}>Del</button>
                  </td>
                )}
              </tr>
            ))}
            {equip.length === 0 && (
              <tr><td colSpan={readOnly ? 5 : 6} style={{ ...styles.td, textAlign: 'center', color: '#9CA3AF' }}>
                No equipment rates extracted.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
